import { pdf } from '@react-pdf/renderer';
import { useCallback, useMemo } from 'react';
import {
  buildRetailInvoiceLabels,
  RetailInvoicePDF,
} from 'src/components/RetailInvoice';
import { useAppContext } from 'src/contexts/AppContext';
import { getContent, useContentContext } from 'src/contexts/ContentContext';
import { useGetGroupInvoiceCatalog } from 'src/hooks/api/useGetGroupInvoiceCatalog';
import { useGetSalesGroup } from 'src/hooks/api/useGetSalesGroup';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { buildGroupInvoiceProps } from 'src/modals/SaleDetails/actions/buildGroupInvoiceProps';
import { MARKETPLACE_PAYMENT_LINE_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { Feature, MarketplacePaymentLineType } from 'src/WebApiController';

/**
 * POS-8856: Shared hook that generates the FULL multi-event GROUP invoice PDF as a
 * Blob for a given salesGroupId. Extracted from LaunchDownloadRetailSaleInvoice so
 * both the SaleDetails download action and the Sales Group modal footer's "View
 * Invoice" (open-in-new-tab) action can reuse the exact same generation logic.
 *
 * Feature gate: this hook is only ever active behind Feature.SalesGroupView. The
 * `shouldUseGroupInvoice` flag ANDs a non-null salesGroupId with
 * useUserHasFeature(Feature.SalesGroupView), and the underlying data hooks
 * (useGetSalesGroup / useGetGroupInvoiceCatalog) self-gate on the same feature — so
 * this satisfies the repo convention that new src/hooks/api/ hooks be feature-gated.
 *
 * Returns `generateBlob` (resolves to the PDF Blob, or null when the group payload
 * isn't available yet or generation fails) and `isPreparing` (true while the group
 * payload or its batch catalog is still in flight, so callers can disable/spin).
 */
export function useGenerateGroupInvoicePdf(
  salesGroupId: string | null | undefined
): { generateBlob: () => Promise<Blob | null>; isPreparing: boolean } {
  const { loginContext } = useAppContext();
  // POS-8856: resolve invoice labels here (hook body has ContentContext) so the group
  // PDF, rendered imperatively outside the provider tree, gets localized strings.
  const contentContext = useContentContext();
  const labels = buildRetailInvoiceLabels(contentContext);

  // POS-8856: resolver for the coarse payment-line type label, passed into the pure
  // buildGroupInvoiceProps (which has no ContentContext). Falls back to the raw enum
  // value only if the map lacks an entry.
  const paymentLineTypeLabel = useCallback(
    (type: MarketplacePaymentLineType) =>
      getContent(
        MARKETPLACE_PAYMENT_LINE_TYPE_TO_CID[type] ?? type,
        contentContext
      ),
    [contentContext]
  );

  // POS-8856: feature gate — the group invoice path only applies when the viewer has
  // Feature.SalesGroupView and the sale belongs to a group.
  const hasSalesGroupViewFeature = useUserHasFeature(Feature.SalesGroupView);
  const shouldUseGroupInvoice =
    salesGroupId != null && hasSalesGroupViewFeature;

  const { data: salesGroup, isFetching: isFetchingSalesGroup } =
    useGetSalesGroup(salesGroupId);

  // Distinct viagogoVirtualIds across the group's member sales so the batch catalog
  // hook can resolve event/venue names. undefined when the group path is inactive so
  // the catalog query stays disabled.
  const groupEventIds = useMemo(
    () =>
      shouldUseGroupInvoice && salesGroup
        ? Array.from(
            new Set(salesGroup.sales.map((s) => s.viagVirtId).filter(Boolean))
          )
        : undefined,
    [shouldUseGroupInvoice, salesGroup]
  );

  const { data: groupCatalog, isFetching: isFetchingGroupCatalog } =
    useGetGroupInvoiceCatalog(groupEventIds);

  const brokerName = loginContext?.user?.activeAccount?.accountName ?? '';
  const brokerEmail = loginContext?.user?.userName ?? undefined;

  const generateBlob = useCallback(async (): Promise<Blob | null> => {
    if (!salesGroup) {
      return null;
    }
    try {
      const props = buildGroupInvoiceProps({
        group: salesGroup,
        brokerName,
        brokerEmail,
        catalog: groupCatalog ?? undefined,
        labels,
        paymentLineTypeLabel,
      });

      return await pdf(
        <RetailInvoicePDF {...props} labels={labels} />
      ).toBlob();
    } catch (error) {
      console.error('Error generating retail group invoice PDF:', error);
      return null;
    }
  }, [
    salesGroup,
    brokerName,
    brokerEmail,
    groupCatalog,
    labels,
    paymentLineTypeLabel,
  ]);

  const isPreparing =
    shouldUseGroupInvoice && (isFetchingSalesGroup || isFetchingGroupCatalog);

  return { generateBlob, isPreparing };
}
